import Enumerable from "linq";
import { IDateStyle, DateFormatKinds, IDateInputBehavior, ITimeStyle, TimeFormatKinds, DateObjectKinds, IObjectNameIdentifier } from "../../../../../../documaker/domain/format/objects";
import { IDateObject } from "../../../../../domain/editableFormat/objects";
import { IDateObjectBean } from "../../beans/objects";
import { AbstractObjectTranslator } from "./AbstractObjectTranslator";
import {
  PrintBehaviorTranslator,
  QuoteBehaviorTranslator,
  EditBehaviorTranslator,
  DateObjectKindTranslator,
  TooltipBehaviorTranslator,
  NaturalTextBehaviorTranslator,
  GroupAlertBehaviorTranslator,
  EmptyAlertBehaviorTranslator,
  LengthAlertBehaviorTranslator,
  TextSearchBehaviorTranslator,
  FreeInputBehaviorTranslator,
  ItemGroupSelectBehaviorTranslator,
  HisInformationItemDefaultSelectBehaviorTranslator,
  HospitalizedHisInformationItemSelectBehaviorTranslator,
  UserDefinedInformationSourceTranslator,
  UserDefinedItemSelectBehaviorTranslator,
  QuestionnaireTranslator,
  RelationReplacer,
  TranscribeBehaviorTranslator,
  TranscribeDateOfTimeBehaviorTranslator,
  EmptyAlertAllowBehaviorTranslator,
  GroupAlertAllowBehaviorTranslator,
  ScriptExecuteTriggerBehaviorTranslator,
  DateCalculateBehaviorTranslator,
} from "./properties";
import { TextDrawableObjectTranslator } from "./base";

export class DateObjectTranslator extends AbstractObjectTranslator<IDateObject, IDateObjectBean> {
  constructor() {
    super(new TextDrawableObjectTranslator());
  }

  protected shouldRecordName(source: IDateObjectBean): boolean {
    return true;
  }

  protected readName(source: IDateObjectBean): IObjectNameIdentifier {
    return source["@name"]
      ? {
        key: source["@name"],
      }
      : {
        name: source["@title"],
      };
  }

  overwriteObject(source: IDateObject, bean: IDateObjectBean, formatId: number): void {
    source.objectKind = this.objectKind.from(bean);
    if (!!bean["@name"]) {
      source.name = bean["@name-sub"];  // サブ名称はHIS項目等でnameを使ってしまうオブジェクトに名称をつけるためのもの.モデルとしてはnameとして扱う.
      source.informationKey = bean["@name"];
    } else {
      source.name = bean["@title"];
      source.informationKey = "";
    }
    source.printBehavior = this.printBehavior.from(bean);
    source.quoteBehavior = this.quoteBehavior.from(bean);
    source.editBehavior = this.editBehavior.from(bean);
    source.tooltipBehavior = this.tooltipBehavior.from(bean);
    source.naturalTextBehavior = this.naturalTextBehavior.from(bean);
    source.groupAlertBehavior = this.groupAlertBehavior.from(bean);
    source.emptyAlertBehavior = this.emptyAlertBehavior.from(bean);
    source.lengthAlertBehavior = this.lengthAlertBehavior.from(bean);
    source.textSearchBehavior = this.textSearchBehavior.from(bean);
    source.freeInputBehavior = this.freeInputBehavior.from(bean);
    source.itemGroupSelectBehavior = this.itemGroupSelectBehavior.from(bean);
    source.hisInformationItemDefaultSelectBehavior = this.hisInformationItemDefaultSelectBehavior.from(bean);
    source.hospitalizedHisInformationItemSelectBehavior = this.hospitalizedHisInformationItemSelectBehavior.from(bean);
    source.userDefinedInformationSource = this.userDefinedInformationSource.from(bean);
    source.userDefinedItemSelectBehavior = this.userDefinedItemSelectBehavior.from(bean);
    source.transcribeBehavior = this.transcribeBehavior.from(bean);
    source.transcribeDateOfTimeBehavior = this.transcribeDateOfTimeBehavior.from(bean);
    source.emptyAlertAllowBehavior = this.emptyAlertAllowBehavior.from(bean);
    source.groupAlertAllowBehavior = this.groupAlertAllowBehavior.from(bean);
    source.scriptExecuteTriggerBehavior = this.scriptExecuteTriggerBehavior.from(bean);
    source.isDate = !bean["@time-format"];
    source.style = source.isDate
      ? this.fromDateFormat(bean)
      : this.fromTimeFormat(bean);
    source.inputBehavior = source.isDate
      ? {
        isFuzzyInputtable: (bean["@approximate-input"] !== "0"),
      }
      : {};
    source.dateCalculateBehavior = this.dateCalculateBehavior.from(bean);
    source.questionnaire = this.questionnaire.from(bean);
    // @ext-keyは対応しない.(大昔にがん登録向けに実装していたが実際に使われてはいない模様.今となってはどう使うかの想定も不明.)
  }

  overwriteBean(source: IDateObjectBean, object: IDateObject): void {
    if (!!object.informationKey) {
      source["@name"] = object.informationKey;
      source["@title"] = undefined;
      source["@name-sub"] = object.name;
    } else {
      source["@name"] = "";
      source["@title"] = object.name;
      source["@name-sub"] = undefined;
    }
    source["@hide"] = this.printBehavior.to(object).hide;
    source["@quote"] = this.quoteBehavior.to(object).quote;
    source["@vanish"] = this.editBehavior.to(object).vanish;
    source["@tooltip"] = this.tooltipBehavior.to(object).tooltip;
    source["@natural-view"] = this.naturalTextBehavior.to(object).naturalView;
    source["@natural-text"] = this.naturalTextBehavior.to(object).naturalText;
    source["@alert-group"] = this.groupAlertBehavior.to(object).alertGroup;
    source["@empty-check"] = this.emptyAlertBehavior.to(object).emptyCheck;
    source["@data-check"] = this.lengthAlertBehavior.to(object).dataCheck;
    source["@length-check"] = this.lengthAlertBehavior.to(object).lengthCheck;
    source["@search-word"] = this.textSearchBehavior.to(object).searchWord;
    source["@free-input-enable"] = this.freeInputBehavior.to(object).freeInputEnable;
    source["@group"] = this.itemGroupSelectBehavior.to(object).group;
    this.relationReplacer.replaceBeans(source, "text-paste", this.transcribeBehavior.to(object.transcribeBehavior));
    this.relationReplacer.replaceBeans(source, "object-link", this.transcribeDateOfTimeBehavior.to(object.transcribeDateOfTimeBehavior));
    this.relationReplacer.replaceBeans(
      source,
      "parent-alert",
      this.emptyAlertAllowBehavior.to(object.emptyAlertAllowBehavior),
      this.groupAlertAllowBehavior.to(object.groupAlertAllowBehavior));
    this.relationReplacer.replaceBeans(source, "script-link-output", this.scriptExecuteTriggerBehavior.to(object.scriptExecuteTriggerBehavior));
    if (object.objectKind === DateObjectKinds.HisItem) {                // HIS情報のみ.(それ以外は前値保持.)
      source["@initial"] = this.hisInformationItemDefaultSelectBehavior.to(object).initial;
      const hospitalizedHisInformationItemSelectBehavior = this.hospitalizedHisInformationItemSelectBehavior.to(object);
      source["@admission"] = object.hisInformationItemDefaultSelectBehavior.isEnable
        ? hospitalizedHisInformationItemSelectBehavior.admission
        : undefined;
      this.relationReplacer.replaceBeans(
        source,
        "base-date",
        object.hospitalizedHisInformationItemSelectBehavior.baseDateReferBehavior.isEnable
          ? hospitalizedHisInformationItemSelectBehavior.relationalBaseDate
          : undefined);
    }
    if (object.objectKind === DateObjectKinds.UserDefined) {            // ユーザ定義情報のみ.(それ以外は前値保持.)
      source["@source-file"] = this.userDefinedInformationSource.to(object).sourceFile;
      const userDefinedItemSelectBehavior = this.userDefinedItemSelectBehavior.to(object);
      source["@select-mode"] = userDefinedItemSelectBehavior.selectMode;
      this.relationReplacer.replaceBeans(
        source,
        "window-type",
        userDefinedItemSelectBehavior.relationalWindowType);
    }
    if (object.objectKind === DateObjectKinds.Normal) {                 // 日付のみ.（それ以外は前回値保持.）
      this.relationReplacer.replaceBeans(
        source,
        "calculate",
        this.dateCalculateBehavior.to(object.dateCalculateBehavior));
    }
    const formats = object.isDate
      ? this.toDateFormat(object.style as IDateStyle)
      : this.toTimeFormat(object.style as ITimeStyle, object.objectKind === DateObjectKinds.HisItem);
    source["@format"] = object.isDate
      ? formats.format
      : "0";
    source["@time-format"] = object.isDate
      ? undefined
      : formats.format;
    source["@visible"] = formats.visible;
    source["@approximate-input"] = (!object.isDate || (object.inputBehavior as IDateInputBehavior).isFuzzyInputtable)
      ? undefined
      : "0";
    this.relationReplacer.replaceBeans(source, "questionnaire", this.questionnaire.to(object));
    this.relationReplacer.replaceBeans(source, "questionnaire-children", this.questionnaire.toChildren(object));
    // @ext-keyは対応しない.(大昔にがん登録向けに実装していたが実際に使われてはいない模様.今となってはどう使うかの想定も不明.)
  }

  private fromDateFormat(source: IDateObjectBean): IDateStyle {
    return this.toFoundValue(
      this._dateFormats,
      p => (p.format === this.integer.from(source["@format"]))
        // 以下の理由から定義との一致は年月日のみマスクして判定する.
        // ・曜日はなしで定義されてるので曜日は見ない.
        // ・年号はv1の新しいバージョンで追加されており、ないものでも読めないとダメなので年号は見ない.(年号の有無はformatでも判断できるので.)
        && ((p.visible & (DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day))
        === (this.integer.from(source["@visible"]) & (DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day))),
      p => ({
        formatKind: p.kind as DateFormatKinds,
        hasWeekDay: ((this.integer.from(source["@visible"]) & DateTimeVisibilityBeans.WeekDay) === DateTimeVisibilityBeans.WeekDay),
      }));
  }

  private toDateFormat(source: IDateStyle): { format: string, visible: string } {
    return this.toFoundValue(
      this._dateFormats,
      p => p.kind === source.formatKind,
      p => ({
        format: this.integer.to(p.format),
        visible: this.integer.to(p.visible | (source.hasWeekDay ? DateTimeVisibilityBeans.WeekDay : 0x0)),
      }));
  }

  private fromTimeFormat(source: IDateObjectBean): ITimeStyle {
    return this.toFoundValue(
      this._timeFormats,
      p => (p.format === this.integer.from(source["@time-format"])) && (p.visible === (this.integer.from(source["@visible"]) & (DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute))),
      p => ({
        formatKind: p.kind as TimeFormatKinds,
      }));
  }

  private toTimeFormat(source: ITimeStyle, isHisItem: boolean): { format: string, visible: string } {
    return this.toFoundValue(
      this._timeFormats,
      p => p.kind === source.formatKind,
      p => ({
        format: this.integer.to(p.format),
        visible: this.integer.to(p.visible | (isHisItem ? DateTimeVisibilityBeans.Second : 0x0)),
      }));
  }

  private toFoundValue<TKind, TValue>(definition: { kind: TKind, format: number, visible: number }[], predicate: (p: { kind: TKind, format: number, visible: number }) => boolean, toValue: (p: { kind: TKind, format: number, visible: number }) => TValue): TValue {
    const found = Enumerable.from(definition) .firstOrDefault(predicate);
    return found
      ? toValue(found)
      : null;
  }

  private readonly _dateFormats: { kind: DateFormatKinds, format: number, visible: number }[] = [
    { kind: DateFormatKinds.ymdStringAd,    format: DateFormatBeans.StringAd,     visible: DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.ymdStringJcEra, format: DateFormatBeans.StringJcEra,  visible: DateTimeVisibilityBeans.Era | DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.ymdStringJc,    format: DateFormatBeans.StringJc,     visible: DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.ymdSlashAd,     format: DateFormatBeans.SlashAd,      visible: DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.ymdSlashJcE,    format: DateFormatBeans.SlashJcE,     visible: DateTimeVisibilityBeans.Era | DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.ymdDotAd,       format: DateFormatBeans.DotAd,        visible: DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.ymdDotJcE,      format: DateFormatBeans.DotJcE,       visible: DateTimeVisibilityBeans.Era | DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.ymStringAd,     format: DateFormatBeans.StringAd,     visible: DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month },
    { kind: DateFormatKinds.ymStringJcEra,  format: DateFormatBeans.StringJcEra,  visible: DateTimeVisibilityBeans.Era | DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month },
    { kind: DateFormatKinds.ymStringJc,     format: DateFormatBeans.StringJc,     visible: DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month },
    { kind: DateFormatKinds.ymDotJcE,       format: DateFormatBeans.DotJcE,       visible: DateTimeVisibilityBeans.Era | DateTimeVisibilityBeans.Year | DateTimeVisibilityBeans.Month },
    { kind: DateFormatKinds.mdStringAd,     format: DateFormatBeans.StringAd,     visible: DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.mdSlashAd,      format: DateFormatBeans.SlashAd,      visible: DateTimeVisibilityBeans.Month | DateTimeVisibilityBeans.Day },
    { kind: DateFormatKinds.jcEra,          format: DateFormatBeans.StringJcEra,  visible: DateTimeVisibilityBeans.Era},
    { kind: DateFormatKinds.jcE,            format: DateFormatBeans.DotJcE,       visible: DateTimeVisibilityBeans.Era},
  ];

  private readonly _timeFormats: { kind: TimeFormatKinds, format: number, visible: number }[] = [
    { kind: TimeFormatKinds.Colon12hEn,     format: TimeFormatBeans.Colon12hEn,   visible: DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute },
    { kind: TimeFormatKinds.Colon24h,       format: TimeFormatBeans.Colon24h,     visible: DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute },
    { kind: TimeFormatKinds.String12hEn,    format: TimeFormatBeans.String12hEn,  visible: DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute },
    { kind: TimeFormatKinds.String12hJa,    format: TimeFormatBeans.String12hJa,  visible: DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute },
    { kind: TimeFormatKinds.String24h,      format: TimeFormatBeans.String24h,    visible: DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute },
    { kind: TimeFormatKinds.Colon12h,       format: TimeFormatBeans.Colon12h,     visible: DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute },
    { kind: TimeFormatKinds.String12h,      format: TimeFormatBeans.String12h,    visible: DateTimeVisibilityBeans.Hour | DateTimeVisibilityBeans.Minute },
  ];

  private readonly printBehavior = new PrintBehaviorTranslator();
  private readonly quoteBehavior = new QuoteBehaviorTranslator();
  private readonly editBehavior = new EditBehaviorTranslator();
  private readonly objectKind = new DateObjectKindTranslator();
  private readonly tooltipBehavior = new TooltipBehaviorTranslator();
  private readonly naturalTextBehavior = new NaturalTextBehaviorTranslator();
  private readonly groupAlertBehavior = new GroupAlertBehaviorTranslator();
  private readonly emptyAlertBehavior = new EmptyAlertBehaviorTranslator();
  private readonly lengthAlertBehavior = new LengthAlertBehaviorTranslator();
  private readonly textSearchBehavior = new TextSearchBehaviorTranslator();
  private readonly freeInputBehavior = new FreeInputBehaviorTranslator();
  private readonly itemGroupSelectBehavior = new ItemGroupSelectBehaviorTranslator();
  private readonly hisInformationItemDefaultSelectBehavior = new HisInformationItemDefaultSelectBehaviorTranslator();
  private readonly hospitalizedHisInformationItemSelectBehavior = new HospitalizedHisInformationItemSelectBehaviorTranslator();
  private readonly userDefinedInformationSource = new UserDefinedInformationSourceTranslator();
  private readonly userDefinedItemSelectBehavior = new UserDefinedItemSelectBehaviorTranslator();
  private readonly transcribeBehavior = new TranscribeBehaviorTranslator();
  private readonly transcribeDateOfTimeBehavior = new TranscribeDateOfTimeBehaviorTranslator();
  private readonly emptyAlertAllowBehavior = new EmptyAlertAllowBehaviorTranslator();
  private readonly groupAlertAllowBehavior = new GroupAlertAllowBehaviorTranslator();
  private readonly scriptExecuteTriggerBehavior = new ScriptExecuteTriggerBehaviorTranslator();
  private readonly questionnaire = new QuestionnaireTranslator();
  private readonly relationReplacer = new RelationReplacer();
  private readonly dateCalculateBehavior = new DateCalculateBehaviorTranslator();
}

enum DateFormatBeans {
  Invisible = 0,
  SlashAd = 1,
  StringAd = 2,
  StringJcEra = 3,
  StringJc = 4,
  DotAd = 5,
  DotJcE = 6,
  SlashJcE = 13,
}

enum TimeFormatBeans {
  Invisible = 0,
  Colon12hEn = 1,
  Colon24h = 2,
  String12hEn = 3,
  String12hJa = 4,
  String24h = 5,
  Colon12h = 6,
  String12h = 7,
}

enum DateTimeVisibilityBeanBits {
  Year = 0,
  Month = 1,
  Day = 2,
  WeekDay = 3,
  Hour = 4,
  Minute = 5,
  Second = 6,
  Era = 7,
  Max = 8,
}

enum DateTimeVisibilityBeans {
  Year = 1 << DateTimeVisibilityBeanBits.Year,
  Month = 1 << DateTimeVisibilityBeanBits.Month,
  Day = 1 << DateTimeVisibilityBeanBits.Day,
  WeekDay = 1 << DateTimeVisibilityBeanBits.WeekDay,
  Hour = 1 << DateTimeVisibilityBeanBits.Hour,
  Minute = 1 << DateTimeVisibilityBeanBits.Minute,
  Second = 1 << DateTimeVisibilityBeanBits.Second,
  Era = 1 << DateTimeVisibilityBeanBits.Era,
}
